import React from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { KnzViewer } from 'components/knz/KnzViewer';
import { INLINE_DEFS, getGroupedComponents } from 'components/knz/embeds/registry';
import { ComponentDef, ComponentRecipe, ParamDef } from 'components/knz/types';

import { COOKBOOK_SAMPLES, CookbookSample } from './cookbook';
import { GUIDE_TOPICS, GuideTopic } from './guideContent';
import { buildParamVariations, paramAnchorId, toPreviewSource } from './referenceUtils';
import { useScrollSpy } from './useScrollSpy';

const LEFT_WIDTH = 260;
const RIGHT_WIDTH = 220;

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <Tooltip title={copied ? 'コピーしました' : 'コピー'}>
      <IconButton
        size="small"
        onClick={() => {
          navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

/** コード風のテンプレート表示 */
const TemplateBlock: React.FC<{ code: string }> = ({ code }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.5,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'action.hover',
      color: 'text.primary',
      fontSize: '0.75rem',
      fontFamily: 'monospace',
      overflow: 'auto',
      whiteSpace: 'pre-wrap',
    }}
  >
    {code}
  </Box>
);

/** ソースが読み物モード（frontmatter に prose: true）かどうか */
const isProsePreview = (source: string): boolean => /^\s*prose\s*:\s*true\s*$/m.test(source);

/** ソース + プレビューの 2 カラム表示 */
const SourceWithPreview: React.FC<{ source: string; showCopy?: boolean }> = ({
  source,
  showCopy,
}) => {
  const prose = isProsePreview(source);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
      <Box>
        {showCopy && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 0.5 }}>
            <CopyButton text={source} />
          </Stack>
        )}
        <TemplateBlock code={source} />
      </Box>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          // 読み物モードは生成りの紙面が白背景に溶けるため、枠の背景を落ち着いた色にして紙面を引き立てる
          bgcolor: prose ? '#EDEAE3' : 'transparent',
        }}
      >
        <KnzViewer source={toPreviewSource(source)} />
      </Box>
    </Box>
  );
};

/** 補足（notes）の表示 */
const Notes: React.FC<{ notes?: string[] }> = ({ notes }) => {
  if (!notes || notes.length === 0) return null;
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderLeft: 3,
        borderLeftColor: 'info.main',
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Stack spacing={0.5}>
        {notes.map((n, i) => (
          <Typography key={i} variant="caption" color="text.secondary">
            {n}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

/** 使い方の例（recipe）の表示 */
const Recipe: React.FC<{ recipe: ComponentRecipe }> = ({ recipe }) => (
  <Box>
    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
      {recipe.title}
    </Typography>
    {recipe.description && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {recipe.description}
      </Typography>
    )}
    <SourceWithPreview source={recipe.source} showCopy />
  </Box>
);

/** param 1 つ分のリファレンスセクション（見出し + 説明 + バリエーションプレビュー） */
const ParamSection: React.FC<{ def: ComponentDef; param: ParamDef }> = ({ def, param }) => {
  const anchor = paramAnchorId(def.name, param.key);
  const variations = buildParamVariations(def, param.key);

  return (
    <Box id={anchor} sx={{ scrollMarginTop: 16 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
          {param.key}
        </Typography>
        {param.required ? (
          <Chip size="small" color="error" label="必須" sx={{ borderRadius: 1, height: 20 }} />
        ) : (
          <Chip size="small" label="任意" variant="outlined" sx={{ borderRadius: 1, height: 20 }} />
        )}
        <Chip
          size="small"
          label={param.type}
          variant="outlined"
          sx={{ borderRadius: 1, height: 20 }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {param.description}
        {param.defaultValue && `（既定値: ${param.defaultValue}）`}
      </Typography>

      {variations.length > 0 ? (
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            値を変えたときの表示:
          </Typography>
          {variations.map((v) => (
            <Box
              key={v.value}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  bgcolor: 'action.hover',
                  borderBottom: 1,
                  borderColor: 'divider',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                }}
              >
                {param.key}:{v.value}
              </Box>
              <KnzViewer source={v.source} />
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.disabled">
          （プレビュー可能なサンプル値はありません）
        </Typography>
      )}
    </Box>
  );
};

/** 選択中コンポーネントの詳細（中央ペイン） */
const ComponentDetail: React.FC<{ def: ComponentDef }> = ({ def }) => {
  return (
    <Stack spacing={3}>
      {/* 概要 */}
      <Box id={`${def.name}--overview`} sx={{ scrollMarginTop: 16 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="h6" fontWeight="bold">
            {def.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {def.label}
          </Typography>
          {def.acceptsChildren && (
            <Chip size="small" label="children 可" variant="outlined" sx={{ borderRadius: 1 }} />
          )}
          {def.textAs && (
            <Chip
              size="small"
              label={`text: ${def.textAs}`}
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {def.description}
        </Typography>
      </Box>

      {/* 基本テンプレート + プレビュー */}
      <Box id={`${def.name}--template`} sx={{ scrollMarginTop: 16 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            基本の書き方
          </Typography>
          <CopyButton text={def.template} />
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
          <TemplateBlock code={def.template} />
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <KnzViewer source={toPreviewSource(def.template)} />
          </Box>
        </Box>
      </Box>

      {/* 補足 */}
      {def.notes && def.notes.length > 0 && (
        <Box id={`${def.name}--notes`} sx={{ scrollMarginTop: 16 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            補足
          </Typography>
          <Notes notes={def.notes} />
        </Box>
      )}

      {/* param ごとのリファレンス */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          パラメータ
        </Typography>
        <Stack spacing={3}>
          {def.params.map((p) => (
            <ParamSection key={p.key} def={def} param={p} />
          ))}
        </Stack>
      </Box>

      {/* 使い方の例 */}
      {def.recipes && def.recipes.length > 0 && (
        <Box id={`${def.name}--recipes`} sx={{ scrollMarginTop: 16 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            使い方の例
          </Typography>
          <Stack spacing={2.5}>
            {def.recipes.map((r, i) => (
              <Recipe key={i} recipe={r} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

/** 応用サンプル（CookbookSample）の詳細表示 */
const CookbookDetail: React.FC<{ sample: CookbookSample }> = ({ sample }) => (
  <Stack spacing={2}>
    <Box id={`${sample.id}--overview`} sx={{ scrollMarginTop: 16 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
        {sample.title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {sample.description}
      </Typography>
    </Box>
    <Box id={`${sample.id}--source`} sx={{ scrollMarginTop: 16 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          ソースとプレビュー
        </Typography>
        <CopyButton text={sample.source} />
      </Stack>
      <SourceWithPreview source={sample.source} />
    </Box>
  </Stack>
);

/** インライン要素一覧の詳細表示 */
const InlineDetail: React.FC = () => (
  <Stack spacing={2}>
    <Box id="inline--overview" sx={{ scrollMarginTop: 16 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
        インライン要素
      </Typography>
      <Typography variant="body2" color="text.secondary">
        text の途中に {'{{name:value}}'} で埋め込む要素です。@userId は {'{{mention:userId}}'}{' '}
        の糖衣です。
      </Typography>
    </Box>
    {INLINE_DEFS.map((d) => (
      <Box
        key={d.name}
        id={`inline--${d.name}`}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, scrollMarginTop: 16 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
            {`{{${d.name}:...}}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {d.label}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {d.description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          value: {d.valueDescription}
          {d.sampleValues &&
            ` / 例: ${d.sampleValues.map((v) => `{{${d.name}:${v}}}`).join(' , ')}`}
        </Typography>
      </Box>
    ))}
  </Stack>
);

/** ツリーの葉（選択項目） */
const TreeItem: React.FC<{
  label: string;
  selected: boolean;
  mono?: boolean;
  onClick: () => void;
}> = ({ label, selected, mono, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      pl: 3,
      pr: 2,
      py: 0.6,
      cursor: 'pointer',
      borderLeft: 2,
      borderColor: selected ? 'primary.main' : 'transparent',
      bgcolor: selected ? 'action.selected' : 'transparent',
      transition: 'background-color 0.15s',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <Typography
      variant="body2"
      fontWeight={selected ? 600 : 400}
      color={selected ? 'primary.main' : 'text.primary'}
      sx={mono ? { fontFamily: 'monospace' } : undefined}
    >
      {label}
    </Typography>
  </Box>
);

/** 開閉できるツリーグループ */
const TreeGroup: React.FC<{
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ label, defaultOpen = true, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 1.5,
          py: 0.6,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: 'text.secondary',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        />
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Collapse in={open}>{children}</Collapse>
    </Box>
  );
};

/** 記法ガイド（GuideTopic）の詳細表示 */
const GuideDetail: React.FC<{ topic: GuideTopic }> = ({ topic }) => (
  <Stack spacing={3}>
    <Box id={`${topic.id}--overview`} sx={{ scrollMarginTop: 16 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
        {topic.title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {topic.summary}
      </Typography>
    </Box>
    {topic.sections.map((s, i) => (
      <Box key={i} id={`${topic.id}--${i}`} sx={{ scrollMarginTop: 16 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          {s.heading}
        </Typography>
        <Stack spacing={0.25} sx={{ mb: 1 }}>
          {s.body.map((line, j) => (
            <Typography key={j} variant="body2" color="text.secondary">
              {line}
            </Typography>
          ))}
        </Stack>
        {s.source &&
          (s.codeOnly ? (
            <TemplateBlock code={s.source} />
          ) : (
            <SourceWithPreview source={s.source} showCopy />
          ))}
      </Box>
    ))}
  </Stack>
);

/**
 * knz リファレンス画面（MUI ドキュメント風の 3 ペイン）。
 * - 左: コンポーネント一覧ツリー
 * - 中央: 選択コンポーネントの詳細（param ごとにバリエーションプレビュー）
 * - 右: ページ内見出し目次（scrollspy）
 */
type Selection =
  | { kind: 'guide'; id: string }
  | { kind: 'component'; name: string }
  | { kind: 'cookbook'; id: string }
  | { kind: 'inline' };

const KnzReference: React.FC = () => {
  const groups = React.useMemo(() => getGroupedComponents(), []);
  const [selected, setSelected] = React.useState<Selection>({
    kind: 'guide',
    id: GUIDE_TOPICS[0]?.id ?? '',
  });
  const contentRef = React.useRef<HTMLDivElement>(null);

  const selectedDef =
    selected.kind === 'component'
      ? groups.flatMap((g) => g.defs).find((d) => d.name === selected.name)
      : undefined;
  const selectedTopic =
    selected.kind === 'guide' ? GUIDE_TOPICS.find((t) => t.id === selected.id) : undefined;
  const selectedSample =
    selected.kind === 'cookbook' ? COOKBOOK_SAMPLES.find((s) => s.id === selected.id) : undefined;
  const isInline = selected.kind === 'inline';

  // 右ペイン目次のアンカー
  const anchors = React.useMemo(() => {
    if (selectedTopic) {
      return [
        { id: `${selectedTopic.id}--overview`, label: '概要' },
        ...selectedTopic.sections.map((s, i) => ({
          id: `${selectedTopic.id}--${i}`,
          label: s.heading,
        })),
      ];
    }
    if (selectedDef) {
      return [
        { id: `${selectedDef.name}--overview`, label: '概要' },
        { id: `${selectedDef.name}--template`, label: '基本の書き方' },
        ...(selectedDef.notes && selectedDef.notes.length > 0
          ? [{ id: `${selectedDef.name}--notes`, label: '補足' }]
          : []),
        ...selectedDef.params.map((p) => ({
          id: paramAnchorId(selectedDef.name, p.key),
          label: p.key,
        })),
        ...(selectedDef.recipes && selectedDef.recipes.length > 0
          ? [{ id: `${selectedDef.name}--recipes`, label: '使い方の例' }]
          : []),
      ];
    }
    if (selectedSample) {
      return [
        { id: `${selectedSample.id}--overview`, label: '概要' },
        { id: `${selectedSample.id}--source`, label: 'ソースとプレビュー' },
      ];
    }
    if (isInline) {
      return [
        { id: 'inline--overview', label: '概要' },
        ...INLINE_DEFS.map((d) => ({ id: `inline--${d.name}`, label: d.name })),
      ];
    }
    return [];
  }, [selectedTopic, selectedDef, selectedSample, isInline]);

  const activeId = useScrollSpy(
    anchors.map((a) => a.id),
    contentRef.current,
  );

  // 選択が変わったら中央を先頭にスクロール
  React.useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [selected]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isSelected = (sel: Selection): boolean => {
    if (selected.kind !== sel.kind) return false;
    if (sel.kind === 'guide' && selected.kind === 'guide') return selected.id === sel.id;
    if (sel.kind === 'cookbook' && selected.kind === 'cookbook') return selected.id === sel.id;
    if (sel.kind === 'component' && selected.kind === 'component')
      return selected.name === sel.name;
    if (sel.kind === 'inline') return true;
    return false;
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* 左: コンポーネント一覧ツリー */}
        <Box
          sx={{
            width: LEFT_WIDTH,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
            py: 1,
          }}
        >
          {/* はじめに（記法ガイド） */}
          <TreeGroup label="はじめに">
            {GUIDE_TOPICS.map((t) => (
              <TreeItem
                key={t.id}
                label={t.title}
                selected={isSelected({ kind: 'guide', id: t.id })}
                onClick={() => setSelected({ kind: 'guide', id: t.id })}
              />
            ))}
          </TreeGroup>

          {/* コンポーネント（グループごと） */}
          {groups.map((g) => (
            <TreeGroup key={g.key} label={g.label}>
              {g.defs.map((d) => (
                <TreeItem
                  key={d.name}
                  label={d.name}
                  selected={isSelected({ kind: 'component', name: d.name })}
                  onClick={() => setSelected({ kind: 'component', name: d.name })}
                />
              ))}
            </TreeGroup>
          ))}

          {/* インライン要素 */}
          <TreeGroup label="インライン要素">
            <TreeItem
              label="インライン要素一覧"
              selected={isSelected({ kind: 'inline' })}
              onClick={() => setSelected({ kind: 'inline' })}
            />
          </TreeGroup>

          {/* 応用サンプル集 */}
          <TreeGroup label="応用サンプル">
            {COOKBOOK_SAMPLES.map((s) => (
              <TreeItem
                key={s.id}
                label={s.title}
                selected={isSelected({ kind: 'cookbook', id: s.id })}
                onClick={() => setSelected({ kind: 'cookbook', id: s.id })}
              />
            ))}
          </TreeGroup>
        </Box>

        {/* 中央: 詳細 */}
        <Box ref={contentRef} sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 3 }}>
          {selectedTopic && <GuideDetail topic={selectedTopic} />}
          {selectedDef && <ComponentDetail def={selectedDef} />}
          {selectedSample && <CookbookDetail sample={selectedSample} />}
          {isInline && <InlineDetail />}
        </Box>

        {/* 右: ページ内目次（scrollspy） */}
        <Box
          sx={{
            width: RIGHT_WIDTH,
            flexShrink: 0,
            borderLeft: 1,
            borderColor: 'divider',
            overflow: 'auto',
            p: 2,
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            このページの内容
          </Typography>
          <Stack spacing={0}>
            {anchors.map((a) => (
              <Link
                key={a.id}
                href={`#${a.id}`}
                underline="none"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo(a.id);
                }}
                sx={{
                  py: 0.5,
                  px: 1,
                  borderLeft: 2,
                  borderColor: activeId === a.id ? 'primary.main' : 'transparent',
                  color: activeId === a.id ? 'primary.main' : 'text.secondary',
                  fontWeight: activeId === a.id ? 600 : 400,
                  fontSize: '0.8rem',
                  transition: 'color 0.15s',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {a.label}
              </Link>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default KnzReference;
